/**
 * Diagrammer DSL — single source of truth for a diagram.
 * Agents (Claude, etc.) generate/edit this JSON directly.
 *
 * DSL Examples:
 *
 * Minimal (2 nodes, 1 edge):
 * {
 *   "id": "simple",
 *   "name": "Simple Flow",
 *   "layers": [{ "id": "default", "name": "Default", "color": "#6366f1" }],
 *   "nodes": [
 *     { "id": "a", "label": "Start", "layer": "default" },
 *     { "id": "b", "label": "End", "layer": "default" }
 *   ],
 *   "edges": [{ "id": "e1", "source": "a", "target": "b" }],
 *   "views": [{ "id": "main", "name": "Main", "layout": "TB" }],
 *   "defaultView": "main"
 * }
 *
 * With preferences:
 * {
 *   "preferences": {
 *     "truncateDescriptions": false,
 *     "showFieldsOnNode": true,
 *     "nodeWidth": 260
 *   },
 *   ...
 * }
 */

/** Top-level rendering preferences. All optional — sensible defaults applied. */
export interface DiagramPreferences {
  truncateDescriptions?: boolean // false = show full text (default: false)
  nodeWidth?: number // px width of nodes (default: 240)
  rankSpacing?: number // px gap between rank tiers (default: 140 TB, 200 LR)
  nodeGap?: number // px gap between nodes in same tier (default: 36)
  edgeStyle?: "smoothstep" | "straight" | "bezier" // default: smoothstep
  edgeColor?: string // hex (default: #3a5575)
  background?: string // canvas bg hex (default: #0b1120)
  showLayerBands?: boolean // colored tier backgrounds (default: true)
  showFieldsOnNode?: boolean // render fields on the node itself (default: false)
  showContextNodes?: boolean // dim nodes outside focused view instead of hiding them (default: false)
  showEdgeLabels?: boolean // render edge labels when present (default: true)
  fitPadding?: number // 0-1, how tight fit-to-view is (default: 0.15)
  maxZoom?: number // cap zoom on fit (default: 1.2)
  animateEdges?: boolean // flowing dash animation on edges (default: true)
  nodeIcon?: boolean // show emoji/icon in node header (default: false)
}

export const DEFAULT_PREFS: Required<DiagramPreferences> = {
  truncateDescriptions: false,
  nodeWidth: 240,
  rankSpacing: 140,
  nodeGap: 36,
  edgeStyle: "smoothstep",
  edgeColor: "#3a5575",
  background: "#0b1120",
  showLayerBands: true,
  showFieldsOnNode: false,
  showContextNodes: false,
  showEdgeLabels: true,
  fitPadding: 0.15,
  maxZoom: 1.2,
  animateEdges: true,
  nodeIcon: false,
}

export function resolvePrefs(p?: DiagramPreferences): Required<DiagramPreferences> {
  return { ...DEFAULT_PREFS, ...p }
}

export interface DiagramLayer {
  id: string
  name: string
  color: string // hex
}

export interface DiagramNode {
  id: string
  label: string
  description?: string
  tooltip?: string // hover-only detail; no tooltip renders unless this is set
  layer: string // references DiagramLayer.id
  type?: string // optional badge label
  fields?: Record<string, string> // key-value metadata shown in detail panel
  rank?: number // layout hint: 0 = top level, higher = deeper
  group?: string // horizontal grouping hint
  html?: string // raw HTML content rendered inside node (local-only, no sanitization)
  icon?: string // emoji or short icon text shown before label
  backgroundColor?: string // optional node/section fill color
  width?: number // custom node width (overrides preferences.nodeWidth)
  height?: number // custom node height (default: auto)
  zIndex?: number // optional manual stacking order for overlapping free nodes
  parentNode?: string // React Flow parent node ID (for group/swimlane layouts)
  position?: { x: number; y: number } // manual position (used when layout is 'manual' or 'radial')
  isGroup?: boolean // marks this node as a group container
}

export interface DiagramEdge {
  id: string
  source: string // DiagramNode.id
  target: string // DiagramNode.id
  label?: string
  style?: "default" | "dashed" | "straight" | "bezier" | "smoothstep"
}

export interface DiagramView {
  id: string
  name: string
  description?: string
  layers?: string[] // if omitted, shows all layers
  nodes?: string[] // optional node allow-list for focused views
  layout: "TB" | "LR" | "manual" | "radial" // TB/LR use dagre, manual/radial use node positions
}

export interface DiagramSpec {
  id: string
  name: string
  description?: string
  preferences?: DiagramPreferences
  layers: DiagramLayer[]
  nodes: DiagramNode[]
  edges: DiagramEdge[]
  views: DiagramView[]
  defaultView: string // DiagramView.id
}

/** Stored in localStorage for recent file history */
export interface RecentDiagram {
  name: string
  fileName: string
  loadedAt: string // ISO timestamp
  spec: DiagramSpec
}
